/**
 * Google Analytics Tracking Utilities
 *
 * This module provides utility functions for tracking events via Google Tag Manager.
 * Events pushed to the dataLayer will be processed by GTM and sent to Google Analytics.
 */

// Type declaration for window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Event categories for consistent tracking
 */
export const EventCategory = {
  USER_INTERACTION: 'User Interaction',
  NAVIGATION: 'Navigation',
  VIDEO: 'Video',
} as const;

/**
 * Event actions for consistent tracking
 */
export const EventAction = {
  CLICK: 'Click',
  CLOSE: 'Close',
} as const;

/** Tracked event names — single source of truth. Every value here MUST have a corresponding GTM trigger + tag. */
export const TrackingEvent = {
  TAB_CLICK: 'tab_click',
  VIDEO_CLICK: 'video_click',
  VIDEO_CLOSE: 'video_close',
  ARTICLE_FILTER_CLICK: 'article_filter_click',
  VIDEO_FILTER_CLICK: 'video_filter_click',
  FIXTURES_FILTER_CLICK: 'fixtures_filter_click',
  RESULTS_FILTER_CLICK: 'results_filter_click',
  SHARE_ARTICLE_CLICK: 'share_article_click',
  NAV_BURGER_MENU_CLICK: 'nav_burger_menu_click',
  SITE_SEARCH: 'site_search',
  NAVIGATION_EVENT: 'navigation_event',
  PAGE_VIEW: 'virtual_page_view',
  ENGAGEMENT_TIME: 'engagement_time',
} as const;

/**
 * Base tracking event parameters
 */
interface TrackingEventParams {
  event_category?: string;
  event_action?: string;
  event_label?: string;
  event_value?: number;
  [key: string]: any; // Allow custom parameters
}

/**
 * Core function to push events to Google Tag Manager's dataLayer
 * @param eventName - The name of the event (this triggers GTM)
 * @param parameters - Optional event parameters
 */
export const trackEvent = (eventName: string, parameters?: TrackingEventParams): void => {
  // Check if we're in a browser environment and dataLayer exists
  if (typeof window !== 'undefined' && window.dataLayer) {
    const eventData = {
      event: eventName,
      event_timestamp: new Date().toISOString(),
      ...parameters,
    };

    window.dataLayer.push(eventData);
  }
};

/**
 * Track navigation events (menu clicks, breadcrumbs, etc.)
 * @param label - Navigation item label
 * @param action - Action type (click, open, close)
 * @param customParams - Additional parameters
 */
export const trackNavigation = (
  label: string,
  action: string = EventAction.CLICK,
  customParams?: Record<string, any>
): void => {
  trackEvent(TrackingEvent.NAVIGATION_EVENT, {
    event_category: EventCategory.NAVIGATION,
    event_action: action,
    event_label: label,
    ...customParams,
  });
};

/**
 * Track page views (useful for SPAs)
 * @param pagePath - The page path
 * @param pageTitle - The page title
 */
/**
 * Track one executed global site search (SC-1096).
 *
 * Fired once per search rather than once per keystroke — the drawer keys it on the debounced term.
 * A search that found nothing is the same event with `result_count: 0`, so zero-result terms are
 * reportable alongside successful ones rather than needing their own event.
 *
 * NOTE: like every value in {@link TrackingEvent}, `site_search` needs a matching GTM trigger and
 * tag before it reaches GA. That is a manual step outside this repo.
 */
export const trackSiteSearch = (searchTerm: string, resultCount: number): void => {
  trackEvent(TrackingEvent.SITE_SEARCH, {
    event_category: EventCategory.USER_INTERACTION,
    event_action: EventAction.CLICK,
    event_label: searchTerm,
    search_term: searchTerm,
    result_count: resultCount,
  });
};

export const trackPageView = (pagePath?: string, pageTitle?: string): void => {
  trackEvent(TrackingEvent.PAGE_VIEW, {
    page_path: pagePath || window.location.pathname,
    page_location: window.location.href,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track e-commerce events
 */
export const trackEcommerce = {
  // View item list (category page, search results)
  viewItemList: (items: any[], listName?: string, listId?: string): void => {
    trackEvent('view_item_list', {
      ecommerce: {
        item_list_name: listName,
        item_list_id: listId,
        items: items,
      },
    });
  },

  // View individual item
  viewItem: (item: any): void => {
    trackEvent('view_item', {
      ecommerce: {
        items: [item],
      },
    });
  },

  // Add to cart
  addToCart: (item: any, quantity: number = 1): void => {
    trackEvent('add_to_cart', {
      ecommerce: {
        items: [
          {
            ...item,
            quantity: quantity,
          },
        ],
      },
    });
  },

  // Remove from cart
  removeFromCart: (item: any, quantity: number = 1): void => {
    trackEvent('remove_from_cart', {
      ecommerce: {
        items: [
          {
            ...item,
            quantity: quantity,
          },
        ],
      },
    });
  },

  // Begin checkout
  beginCheckout: (items: any[], value: number, currency: string = 'USD'): void => {
    trackEvent('begin_checkout', {
      ecommerce: {
        value: value,
        currency: currency,
        items: items,
      },
    });
  },

  // Purchase
  purchase: (transactionData: any): void => {
    trackEvent('purchase', {
      ecommerce: transactionData,
    });
  },
};

/**
 * Track engagement time on page
 * Call this when the user leaves the page or at regular intervals
 */
export const trackEngagementTime = (seconds: number): void => {
  trackEvent(TrackingEvent.ENGAGEMENT_TIME, {
    event_category: EventCategory.USER_INTERACTION,
    event_action: 'Time on Page',
    event_value: seconds,
    engagement_time_seconds: seconds,
  });
};

/**
 * Video tracking utilities
 */
export const trackVideo = {
  /**
   * Track when a user clicks on a video thumbnail to open the video overlay
   * @param videoId - The video ID (e.g., YouTube video ID)
   * @param videoTitle - The title of the video
   * @param videoUrl - The full URL to the video
   */
  thumbnailClick: (videoId: string, videoTitle: string, videoUrl: string): void => {
    trackEvent(TrackingEvent.VIDEO_CLICK, {
      event_category: EventCategory.VIDEO,
      event_action: EventAction.CLICK,
      event_label: videoTitle,
      video_id: videoId,
      video_title: videoTitle,
      video_url: videoUrl,
    });
  },

  /**
   * Track when a user closes the video overlay
   * @param videoId - The video ID
   * @param videoTitle - The title of the video
   * @param videoUrl - The full URL to the video
   * @param viewDurationSeconds - How long the overlay was open in seconds
   */
  overlayClose: (videoId: string, videoTitle: string, videoUrl: string, viewDurationSeconds: number): void => {
    trackEvent(TrackingEvent.VIDEO_CLOSE, {
      event_category: EventCategory.VIDEO,
      event_action: EventAction.CLOSE,
      event_label: videoTitle,
      event_value: Math.round(viewDurationSeconds),
      video_id: videoId,
      video_title: videoTitle,
      video_url: videoUrl,
      video_view_duration_seconds: Math.round(viewDurationSeconds),
    });
  },
};

/**
 * Track tab click events
 * @param tabLabel - The label of the clicked tab
 */
export const trackTabClick = (tabLabel: string): void => {
  trackEvent(TrackingEvent.TAB_CLICK, {
    event_category: EventCategory.USER_INTERACTION,
    event_action: EventAction.CLICK,
    event_label: tabLabel,
    tab_label: tabLabel,
  });
};

/**
 * Track share article button clicks
 * @param sharedUrl - The URL that was copied to clipboard
 */
export const trackShareArticle = (sharedUrl: string): void => {
  trackEvent(TrackingEvent.SHARE_ARTICLE_CLICK, {
    event_category: EventCategory.USER_INTERACTION,
    event_action: EventAction.CLICK,
    event_label: 'Share Article',
    shared_url: sharedUrl,
  });
};

/**
 * Track navigation burger menu toggle
 * @param action - Whether the menu is being opened or closed
 */
export const trackNavBurgerMenu = (action: 'open' | 'close'): void => {
  trackEvent(TrackingEvent.NAV_BURGER_MENU_CLICK, {
    event_category: EventCategory.USER_INTERACTION,
    event_action: EventAction.CLICK,
    event_label: action === 'open' ? 'openNavBurgerMenu' : 'closeNavBurgerMenu',
  });
};

/**
 * Article filter params for tracking
 */
export interface ArticleFilterParams {
  divisions: string[];
  clubs: string[];
  search: string;
  tags: string[];
}

/**
 * Match filter params for tracking (fixtures/results)
 */
export interface MatchFilterParams {
  matchType: 'fixtures' | 'results' | 'all';
  division: string;
  club: string;
  team: string;
  startDate: string;
  endDate: string;
  sort: string;
}

/**
 * Article and video filter bars share the same shape (division, club, search,
 * tags), so they share one payload builder — only the event and label differ.
 */
const buildFilterPayload = (filters: ArticleFilterParams, eventLabel: string) => ({
  event_category: EventCategory.USER_INTERACTION,
  event_action: EventAction.CLICK,
  event_label: eventLabel,
  filter_divisions: filters.divisions.join(','),
  filter_divisions_count: filters.divisions.length,
  filter_clubs: filters.clubs.join(','),
  filter_clubs_count: filters.clubs.length,
  filter_search: filters.search,
  filter_tags: filters.tags.join(','),
  filter_tags_count: filters.tags.length,
  has_filters:
    filters.divisions.length > 0 || filters.clubs.length > 0 || filters.search !== '' || filters.tags.length > 0,
});

/**
 * Filter tracking utilities
 */
export const trackFilter = {
  /**
   * Track when article filters are applied (sends all filter params at once)
   * @param filters - All current filter values
   */
  articleFilterComplete: (filters: ArticleFilterParams): void => {
    trackEvent(TrackingEvent.ARTICLE_FILTER_CLICK, buildFilterPayload(filters, 'article_filters'));
  },

  /**
   * Track when video filters are applied (sends all filter params at once)
   * @param filters - All current filter values
   */
  videoFilterComplete: (filters: ArticleFilterParams): void => {
    trackEvent(TrackingEvent.VIDEO_FILTER_CLICK, buildFilterPayload(filters, 'video_filters'));
  },

  /**
   * Track when match filters are applied (for fixtures or results)
   * @param matchType - The current match type context ('fixtures', 'results', 'all')
   * @param filterType - The type of filter being applied
   * @param selections - The selected filter values
   */
  matchFilterComplete: (matchType: 'fixtures' | 'results' | 'all', filterType: string, selections: string[]): void => {
    // Determine event name based on match type
    const eventName =
      matchType === 'results' ? TrackingEvent.RESULTS_FILTER_CLICK : TrackingEvent.FIXTURES_FILTER_CLICK;

    trackEvent(eventName, {
      event_category: EventCategory.USER_INTERACTION,
      event_action: EventAction.CLICK,
      event_label: filterType,
      filter_type: filterType,
      filter_selections: selections.join(','),
      filter_count: selections.length,
      match_type: matchType,
    });
  },

  /**
   * Track when match filters are submitted (all filters at once)
   * @param filters - All current filter values
   */
  matchFiltersSubmit: (filters: MatchFilterParams): void => {
    // Determine event name based on match type
    const eventName =
      filters.matchType === 'results' ? TrackingEvent.RESULTS_FILTER_CLICK : TrackingEvent.FIXTURES_FILTER_CLICK;

    trackEvent(eventName, {
      event_category: EventCategory.USER_INTERACTION,
      event_action: EventAction.CLICK,
      event_label: 'match_filters',
      match_type: filters.matchType,
      filter_division: filters.division,
      filter_club: filters.club,
      filter_team: filters.team,
      filter_start_date: filters.startDate,
      filter_end_date: filters.endDate,
      filter_sort: filters.sort,
      has_filters:
        filters.division !== 'all' ||
        filters.club !== 'all' ||
        filters.team !== 'all' ||
        filters.startDate !== '' ||
        filters.endDate !== '' ||
        filters.sort !== 'default',
    });
  },
};
